import { HyperBackendMethod } from "@shared/backend/hyper-backend-method";
import { Roles } from "@shared/Roles";
import { Allow, Controller, dbNamesOf, repo, SqlDatabase } from "remult";
import { AidRequestForm } from "../aid-request-form";
import { AssociationTeam } from "../association-team";
import { Group } from "../group";
import { Mission } from "../mission";
import { Opportunity } from "../opportunity";
import { SubTeam } from "../sub-team";
import { TeamManager } from "../team-manager";
import { TeamOpportunity } from "../team-opportunity";
import { Team } from "./entity";
import { resolveTeamDeleteFallback } from "@shared/helpers/team-delete-fallback";

@Controller("team-methods")
export class TeamMethods {
  @HyperBackendMethod({
    allowed: Allow.authenticated,
    overrideSecurity: true,
  })
  static async getRecursiveSubTeamIds(_team: Team | string) {
    const cmd = SqlDatabase.getDb().createCommand();
    const teamId = typeof _team === "string" ? _team : _team.id;
    const team = await dbNamesOf(Team, { tableName: false });
    const subTeam = await dbNamesOf(SubTeam, { tableName: true });

    const query = `
    WITH RECURSIVE team_hierarchy AS (
      SELECT
        ${team.id}
      FROM
        ${team}
      WHERE
        ${team.id} = ${cmd.param(teamId)}
      UNION ALL
      SELECT
        ${subTeam.subTeamId}
      FROM
        team_hierarchy th
      JOIN
        ${subTeam} ON ${subTeam.teamId} = ${team.id.replace("team", "th")}
    )
    SELECT th."id"
    FROM team_hierarchy th
  `;

    const result = await cmd.execute(query);
    const ids: string[] = result.rows
      .map((v) => v.id)
      .filter((v) => v !== teamId);
    return ids;
  }

  @HyperBackendMethod({
    allowed: true,
    apiPrefix: "team-methods",
    overrideSecurity: true,
  })
  static async getName(teamId: string) {
    const team = await repo(Team).findId(teamId);
    return team?.name;
  }

  @HyperBackendMethod({
    allowed: true,
    apiPrefix: "team-methods",
    overrideSecurity: true,
  })
  static async doesExist(id: string) {
    const exists = await repo(Team).findId(id);
    return !!exists;
  }

  @HyperBackendMethod({
    allowed: true,
    apiPrefix: "team-methods",
    overrideSecurity: true,
  })
  static async isOrganization(params: {
    teamId: string;
    organizationId: string;
  }) {
    const { organizationId, teamId } = params;
    const team = await repo(Team).findId(teamId, {
      include: {
        organization: true,
      },
    });
    if (!team || team.organization?.id !== organizationId) {
      return false;
    }
    return true;
  }

  @HyperBackendMethod({
    allowed: true,
    apiPrefix: "team-methods",
    overrideSecurity: true,
  })
  static async getOrganizationId(teamId: string) {
    const team = await repo(Team).findId(teamId, {
      include: {
        organization: true,
      },
    });
    return team?.organization?.id;
  }

  @HyperBackendMethod({
    allowed: Roles.AdminsAndManager,
    apiPrefix: "team-methods",
    overrideSecurity: true,
    transactional: true,
  })
  static async deleteTeamsAndReassignOpportunities(params: {
    teamIds: string[];
    fallbackTeamId?: string | null;
  }): Promise<{ teamIds: string[] }> {
    const requestedIds = [...new Set((params.teamIds ?? []).filter(Boolean))];
    if (requestedIds.length === 0) {
      return { teamIds: [] };
    }

    const teamIds = new Set(requestedIds);
    for (const teamId of requestedIds) {
      const childIds = await TeamMethods.getRecursiveSubTeamIds(teamId);
      for (const childId of childIds) teamIds.add(childId);
    }
    const ids = [...teamIds];
    const teamIdSet = new Set(ids);
    const fallbackTeamId = resolveTeamDeleteFallback(
      ids,
      params.fallbackTeamId,
    );
    const pageSize = 200;
    const opportunities: Opportunity[] = [];
    let page = 1;
    for (;;) {
      const batch = await repo(Opportunity).find({
        where: Opportunity.isInOneOfTheseTeams({ teams: ids }),
        include: { teams: true },
        limit: pageSize,
        page,
      });
      opportunities.push(...batch);
      if (batch.length < pageSize) break;
      page += 1;
    }

    for (const opportunity of opportunities) {
      const remaining = (opportunity.teams ?? []).filter(
        (team) => !teamIdSet.has(team.teamId),
      );
      const updatedTeams =
        remaining.length > 0
          ? remaining
          : fallbackTeamId
            ? [{ teamId: fallbackTeamId } as TeamOpportunity]
            : [];
      await repo(Opportunity).update(opportunity.id, {
        teams: updatedTeams,
      });
    }

    await repo(SubTeam).deleteMany({
      where: {
        $or: [{ teamId: { $in: ids } }, { subTeamId: { $in: ids } }],
      },
    });
    await repo(TeamOpportunity).deleteMany({
      where: { teamId: { $in: ids } },
    });
    await repo(TeamManager).deleteMany({
      where: { teamId: { $in: ids } },
    });
    await repo(AssociationTeam).deleteMany({
      where: { teamId: { $in: ids } },
    });

    if (fallbackTeamId) {
      await repo(Group).updateMany({
        where: { teamId: { $in: ids } },
        set: { teamId: fallbackTeamId },
      });
      await repo(AidRequestForm).updateMany({
        where: { teamId: { $in: ids } },
        set: { teamId: fallbackTeamId },
      });
    } else {
      const [groupCount, formCount] = await Promise.all([
        repo(Group).count({ teamId: { $in: ids } }),
        repo(AidRequestForm).count({ teamId: { $in: ids } }),
      ]);
      if (groupCount > 0 || formCount > 0) {
        throw new Error(
          "לא ניתן למחוק פרויקט שמשויכות אליו קבוצות או טפסי בקשה בלי פרויקט ראשי לשייך אליו",
        );
      }
    }

    await repo(Mission).updateMany({
      where: { teamId: { $in: ids } },
      set: { teamId: null },
    });

    await repo(Team).deleteMany({ where: { id: { $in: ids } } });
    return { teamIds: ids };
  }
}
